import axios from 'axios';
import { destroy } from 'redux-form';
import { BASE_API, USER_TOKEN } from 'config/consts';
import {
  buildStationPayload,
  getDefaultStationId,
  isClubsStationContext,
  resolveStationIds,
} from './clubsMultiStation';

const URL_ACTION = `${BASE_API}/actions`;
const URL_ACTION_MULTI_V2 = `${BASE_API}/v2/actions/multi-station`;

const authHeaders = () => ({
  Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
});

const showError = (dispatch, message) => {
  dispatch({
    type: 'SNACKBAR',
    payload: {
      message: String(message || 'Erro ao publicar'),
      variant: 'error',
      open: true,
    },
  });
};

const showSuccess = (dispatch, message) => {
  dispatch({
    type: 'SNACKBAR',
    payload: { message, variant: 'success', open: true },
  });
};

/** Erro estruturado do v2: { station_id, step, message, details } => texto legível. */
const formatV2Error = (error) => {
  if (!error) return 'Erro ao publicar';
  const station = error.details?.station_name || error.station_id;
  const prefix = station ? `Station ${station}` : 'Publicação';
  const step = error.step ? ` (etapa: ${error.step})` : '';
  return `${prefix}${step}: ${error.message}`;
};

const extractErrorMessage = (e) => {
  const status = e?.response?.status;
  if (status === 502 || status === 504) {
    return 'O servidor demorou para responder ao publicar em múltiplas stations. Tente novamente.';
  }

  const data = e?.response?.data;
  if (Array.isArray(data?.messages) && data.messages.length > 0) {
    return data.messages.join(' | ');
  }
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors.map(formatV2Error).join(' | ');
  }
  if (Array.isArray(data)) {
    return data.join(', ');
  }
  if (data?.message) {
    return data.message;
  }
  if (data?.messages) {
    return Array.isArray(data.messages) ? data.messages.join(', ') : String(data.messages);
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const firstKey = Object.keys(data)[0];
    if (firstKey && typeof data[firstKey] === 'string') {
      return data[firstKey];
    }
  }
  if (typeof data === 'string' && data.length < 300 && !data.includes('<')) {
    return data;
  }
  return e.message || 'Erro ao publicar';
};

/** POST único no v2: cria na station pai e libera acesso às filhas. */
const postMultiStationV2 = async ({
  parentStationId,
  parentActionId,
  stationIds,
  preparePayload,
}) => {
  const stations = stationIds.map((stationId) => ({
    station_id: stationId,
    payload: preparePayload(stationId),
  }));

  const body = {
    parent_station_id: parentStationId,
    stations,
  };
  if (parentActionId) {
    body.parent_action_id = parentActionId;
  }

  const response = await axios.post(URL_ACTION_MULTI_V2, body, {
    headers: authHeaders(),
  });

  return response.data; // { ok, results, errors, messages, hierarchy_children }
};

const finishDispatch = (dispatch, formName) => {
  dispatch(
    [
      { type: 'ACTIVE_ACTIONS_BUTTON_LOAD', payload: false },
      { type: 'SUBMIT_PROGRESS', payload: 0 },
      formName ? destroy(formName) : null,
    ].filter(Boolean)
  );
};

const reportV2Outcome = (dispatch, outcome, { successMessage } = {}) => {
  const published = (outcome?.results || []).filter(
    (r) => r.status === 'created' || r.status === 'granted'
  );

  if (published.length > 0 && successMessage) {
    showSuccess(dispatch, successMessage(published.length));
  }

  if (outcome && !outcome.ok) {
    const msg =
      Array.isArray(outcome.messages) && outcome.messages.length > 0
        ? outcome.messages.join(' | ')
        : (outcome.errors || []).map(formatV2Error).join(' | ');
    if (msg) {
      showError(dispatch, msg);
    }
  }

  return published;
};

/**
 * Publica/cria a mesma atividade em múltiplas stations via endpoint v2
 * (uma Action no pai; filhas só ganham acesso — erros por station/etapa).
 *
 * Usado pelos modais de engajamento Clubs:
 * - Post (16), Cupom (3), Redirecionamento (13), Link externo (20), Formulário (8)
 *
 * - create: v2 cria no Clubs e libera as filhas selecionadas
 * - update/rascunho: atualiza a Action (PUT) e libera as demais via v2
 */
export const submitActionMultiStation = ({
  values,
  router,
  createOrUpdate,
  actionId,
  publishAction,
  formName,
  auth,
  onBeforeEachStation,
}) => {
  return async (dispatch) => {
    dispatch([
      { type: 'ACTIVE_ACTIONS_BUTTON_LOAD', payload: true },
      { type: 'SUBMIT_PROGRESS', payload: 1 },
    ]);

    const defaultStationId = getDefaultStationId(auth);
    // Fora do contexto Clubs, publica apenas na station ativa (comportamento legado)
    const stationIds = isClubsStationContext(auth)
      ? resolveStationIds(values, defaultStationId)
      : [defaultStationId].filter(Boolean);

    if (!stationIds.length) {
      dispatch([
        { type: 'ACTIVE_ACTIONS_BUTTON_LOAD', payload: false },
        { type: 'SUBMIT_PROGRESS', payload: 0 },
      ]);
      showError(dispatch, 'Selecione ao menos uma Station para publicar a atividade');
      return;
    }

    const preparePayload = (stationId) => {
      let payload = buildStationPayload(values, stationId);
      if (typeof onBeforeEachStation === 'function') {
        payload = onBeforeEachStation(payload, stationId) || payload;
      }
      return payload;
    };

    try {
      if (createOrUpdate === 'update') {
        const existingStationId = values.station_id || values.station || stationIds[0];
        const primaryStationId = stationIds.includes(existingStationId)
          ? existingStationId
          : stationIds[0];

        const updatePayload = preparePayload(primaryStationId);
        await axios.put(`${URL_ACTION}/${actionId}`, updatePayload, {
          headers: authHeaders(),
        });

        dispatch({ type: 'SUBMIT_PROGRESS', payload: '50 %' });

        const extraStations = stationIds.filter((id) => id !== primaryStationId);
        let outcome = null;
        if (extraStations.length > 0) {
          const preparedChild = (stationId) => {
            const payload = preparePayload(stationId);
            payload.is_draft = false;
            payload.active = true;
            return payload;
          };
          outcome = await postMultiStationV2({
            parentStationId: null,
            parentActionId: actionId,
            stationIds: extraStations,
            preparePayload: preparedChild,
          });
        }

        if (publishAction) {
          dispatch([publishAction()]);
        } else {
          finishDispatch(dispatch, formName);
          if (router) {
            router.push('/owner/acoes/minhas-atividades');
          }
        }

        if (outcome) {
          reportV2Outcome(dispatch, outcome, {
            successMessage: () => 'Atividade atualizada e liberada nas entidades selecionadas',
          });
        } else {
          showSuccess(dispatch, 'Atividade publicada com sucesso');
        }
        return;
      }

      // Create: pai + filhas em UMA request. Pai = station ativa se selecionada.
      const parentStationId = stationIds.includes(defaultStationId)
        ? defaultStationId
        : stationIds[0];

      dispatch({ type: 'SUBMIT_PROGRESS', payload: '30 %' });

      const outcome = await postMultiStationV2({
        parentStationId,
        stationIds,
        preparePayload,
      });

      dispatch({ type: 'SUBMIT_PROGRESS', payload: '100 %' });

      const published = reportV2Outcome(dispatch, outcome, {
        successMessage: () => 'Atividade publicada no Clubs e liberada nas entidades selecionadas',
      });

      dispatch([
        { type: 'ACTIVE_ACTIONS_BUTTON_LOAD', payload: false },
        { type: 'SUBMIT_PROGRESS', payload: 0 },
      ]);

      if (published.length > 0) {
        if (formName) {
          dispatch(destroy(formName));
        }
        if (router) {
          router.push('/owner/acoes/minhas-atividades');
        }
      }
    } catch (e) {
      dispatch([
        { type: 'ACTIVE_ACTIONS_BUTTON_LOAD', payload: false },
        { type: 'SUBMIT_PROGRESS', payload: 0 },
      ]);
      showError(dispatch, extractErrorMessage(e));
    }
  };
};
